import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export function useAdmin() {
  const { user } = useAuth();

  const { data: adminData, isLoading } = useQuery({
    queryKey: ["user-role", user?.id],
    queryFn: async () => {
      if (!user) return { isAdmin: false, isStaff: false, role: null };

      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      if (error) throw error;
      
      // Get the highest privilege role (priority order: super_admin > admin > manager > editor > viewer > customer)
      const rolePriority = ['super_admin', 'admin', 'manager', 'editor', 'viewer', 'customer'] as const;
      const roles = data?.map(r => r.role) || [];
      const highestRole = rolePriority.find(r => roles.includes(r)) || null;
      
      const isSuperAdmin = highestRole === 'super_admin';
      const isStaff = highestRole ? ['super_admin', 'admin', 'manager', 'editor', 'viewer'].includes(highestRole) : false;
      const isAdmin = highestRole === 'super_admin' || highestRole === 'admin' || highestRole === 'manager';
      
      return { isAdmin, isStaff, isSuperAdmin, role: highestRole };
    },
    enabled: !!user,
  });

  return { 
    isAdmin: adminData?.isAdmin ?? false, 
    isStaff: adminData?.isStaff ?? false,
    isSuperAdmin: adminData?.isSuperAdmin ?? false,
    role: adminData?.role ?? null,
    isLoading 
  };
}
